import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useSupabaseChatStore } from '@/hooks/useSupabaseChatStore';
import SimpleChatHeader from '@/components/chat/SimpleChatHeader';
import SimpleChatMessageList from '@/components/chat/SimpleChatMessageList';
import SimpleChatInput from '@/components/chat/SimpleChatInput';
import ModelSelector from '@/components/chat/ModelSelector';
import ChatDialogs from '@/components/chat/ChatDialogs';
import CollabConfigModal from '@/components/chat/CollabConfigModal';
import CollabMessageCard from '@/components/chat/CollabMessageCard';
import LocalModelStatusCard from '@/components/chat/LocalModelStatusCard';
import { Attachment, Message, AIModel, Chat as ChatType } from '@/types';
import { toast } from "@/hooks/use-toast";
import { useSupabaseStore } from '@/store/SupabaseStore';
import { FileText, Info, Presentation, Settings2, Sheet, Users } from 'lucide-react';

import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { enableChatVFSAccess, disableChatVFSAccess } from '@/lib/osVFSBridge';
import { useAppStore } from '@/store';

import { useAuth } from '@/contexts/AuthContext';
import { useLocalLLM } from '@/contexts/LocalLLMContext';
import { sendMessageToModel, type AIConfig } from '@/services/ModelService';

import { v4 as uuidv4 } from 'uuid';
import { useCodeSnippets } from '@/hooks/supabase/useCodeSnippets';
import { supabase } from '@/integrations/supabase/client';
import { useIsMobile } from '@/hooks/use-mobile';
import TypingIndicator from '@/components/chat/TypingIndicator';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cleanMarkdownBold } from '@/lib/utils';
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { getProjectAiContext } from '@/lib/projectAiContext';
import { appendGemma4OnnxModel } from '@/lib/gemma4Onnx';
import { filterEnabledModels } from '@/lib/modelSettings';
import { appendChatMessageIfEnabled } from '@/lib/osIntegration';
import { loadThinkingEnabled, modelSupportsThinking, saveThinkingEnabled } from '@/lib/chatThinking';
import {
  createDefaultCollabConfig,
  getCollabSummary,
  normalizeCollabConfig,
  validateCollabConfig,
} from '@/components/chat/collabTypes';

const isValidUUID = (uuid: string): boolean => {
  if (!uuid) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

interface ChatProps {
  chatIdOverride?: string;
  embeddedInMapleOS?: boolean;
  onInvalidChat?: (chatId?: string) => void;
}

const Chat: React.FC<ChatProps> = ({
  chatIdOverride,
  embeddedInMapleOS = false,
  onInvalidChat,
}) => {
  const { chatId: routeChatId } = useParams<{ chatId: string }>();
  const chatId = chatIdOverride ?? routeChatId;
  const navigate = useNavigate();
  const location = useLocation();
  const isEmbeddedChat = embeddedInMapleOS || new URLSearchParams(location.search).get('embeddedInMapleOS') === 'true';
  const { incrementChatViews, addMessage: addMessageToDb, addTokensUsed, models: baseAvailableModels } = useSupabaseStore();
  const availableModels = useMemo(() => filterEnabledModels(appendGemma4OnnxModel(baseAvailableModels)), [baseAvailableModels]);

  const viewCountTracked = useRef(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [pendingArena, setPendingArena] = useState(false);
  const [pendingCollab, setPendingCollab] = useState(false);
  const [isCollabConfigOpen, setIsCollabConfigOpen] = useState(false);
  const isMobile = useIsMobile();
  const [thinkingEnabled, setThinkingEnabled] = useState(false);

  const { user: authUser } = useAuth();
  const appUser = useAppStore((state) => state.user);
  const { status: localLLMStatus } = useLocalLLM();

  useEffect(() => {
    if (!chatId) {
      const params = new URLSearchParams(location.search);
      const preferModel = params.get('preferModel');
      const preferredByRoute = preferModel ? availableModels.find(m => m.id === preferModel) : null;
      const preferredByProfile = appUser?.defaultModelApiName
        ? availableModels.find(m => m.apiName === appUser.defaultModelApiName)
        : null;
      const fallback = preferredByRoute || preferredByProfile || availableModels.find(m => m.tier === 'free') || availableModels[0];

      if (authUser && fallback) {
        (async () => {
          const newChat = await useSupabaseStore.getState().addChat(authUser.id, 'New Chat', fallback.id);
          if (newChat?.id) {
            navigate(`/chat/${newChat.id}`);
          } else {
            toast({
              title: "Chat creation failed",
              description: "Could not create a new chat.",
              variant: "destructive"
            });
          }
        })();
      } else if (authUser && availableModels.length > 0) {
        toast({
          title: "No models available",
          description: "Could not find a model to start the chat.",
          variant: "destructive"
        });
      }
    }
  }, [chatId, location.search, authUser, appUser?.defaultModelApiName, availableModels, navigate]);

  const {
    messages,
    isLoading,
    isGenerating,
    queuedMessages,
    activeRequestChatId,
    chatTitle,
    modelName,
    selectedModelId,
    modelId2: arenaSecondModelId,
    collabConfig,
    isUsingKnowledgeBase,
    sendMessage,
    removeQueuedMessage,
    stopActiveResponse,
    runDeepResearchCollab,
    handleTitleChange,
    handleDeleteChat,
    handleModelChange,
    handleModel2Change,
    saveCollabConfig,
    clearMessages,
    initializeChat,
    setMessages,
    isMemoryUpdating,
  } = useSupabaseChatStore();

  const { chats, updateChat } = useSupabaseStore();
  const currentChat = useMemo(() => {
    return chats.find(chat => chat.id === chatId);
  }, [chats, chatId]);
  const currentProjectId = currentChat?.project_id;
  const [projectKbId, setProjectKbId] = useState<string | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const selectedModel = useMemo(
    () => availableModels.find((model) => model.id === selectedModelId) || null,
    [availableModels, selectedModelId]
  );
  const thinkingAvailable = modelSupportsThinking(selectedModel);
  const isLocalBrowserModel = selectedModel?.provider === 'local' || selectedModel?.runtime === 'webllm';
  const showLocalModelBanner = isLocalBrowserModel && messages.length > 0 && ['downloading', 'initializing', 'error', 'webgpu_not_supported'].includes(localLLMStatus.state);

  const visibleSwitchClassName = 'border-slate-300 data-[state=unchecked]:bg-slate-200 dark:border-slate-600 dark:data-[state=unchecked]:bg-slate-700';

  const headerToggleCardClassName = 'flex items-center gap-2 rounded-md border bg-card/80 px-2 py-1 shadow-sm';

  useEffect(() => {
    setThinkingEnabled(loadThinkingEnabled(selectedModel?.id ?? null));
  }, [selectedModel?.id]);

  useEffect(() => {
    if (!selectedModel || !isLocalBrowserModel) {
      return;
    }

    void import('@/llm/localTinyLlamaManager')
      .then(({ localLLM }) => localLLM.init(selectedModel))
      .catch((error) => {
        console.error('[Chat] selected local model init failed:', error);
      });
  }, [selectedModel, isLocalBrowserModel]);

  useEffect(() => {
    const ensureKb = async () => {

      if (!currentProjectId || !authUser) {
        setProjectKbId(null);
        setProjectName(null);
        return;
      }
      const { data: projData } = await (supabase as any)
        .from('projects')
        .select('id, name')
        .eq('id', currentProjectId)
        .maybeSingle();
      const proj: any = projData;
      const name = proj?.name || 'Project';
      setProjectName(name);

      const kbName = `Project ${currentProjectId} KB`;
      const { data: kbExistingData } = await (supabase as any)
        .from('knowledge_bases')
        .select('id')
        .eq('created_by', authUser.id)
        .eq('name', kbName)
        .maybeSingle();

      const kbExisting: any = kbExistingData;
      let kbId = kbExisting?.id || null;
      if (!kbId) {
        const { data: kbNewData, error } = await (supabase as any)
          .from('knowledge_bases')
          .insert({
            name: kbName,
            description: `Knowledge base for project ${currentProjectId}`,
            organization_id: null,
            created_by: authUser.id,
            is_private: true,
          })
          .select('id')
          .single();
        const kbNew: any = kbNewData;
        if (!error) kbId = kbNew?.id || null;
      }
      setProjectKbId(kbId);
    };
    ensureKb();
  }, [currentProjectId, authUser?.id]);

  const arenaMode = currentChat?.type === 'arena';
  const collabMode = currentChat?.type === 'collab';

  const normalizedCollabConfig = useMemo(
    () => normalizeCollabConfig(collabConfig || currentChat?.collabConfig || null, availableModels, selectedModelId || undefined),
    [availableModels, collabConfig, currentChat?.collabConfig, selectedModelId]
  );
  const collabSummary = useMemo(
    () => getCollabSummary(normalizedCollabConfig, availableModels),
    [normalizedCollabConfig, availableModels]
  );
  const collabValidation = useMemo(
    () => validateCollabConfig(normalizedCollabConfig, availableModels),
    [normalizedCollabConfig, availableModels]
  );
  const deepResearchEnabled = normalizedCollabConfig.workflow === 'deep_research';

  const chatInGlobalStore = useSupabaseStore(state =>
    state.chats.find(c => c.id === chatId)
  );

  useEffect(() => {
    if (chatInGlobalStore?.messages) {
      setMessages(chatInGlobalStore.messages);
    }
  }, [chatInGlobalStore?.messages, setMessages]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId)) {
      initializeChat(chatId);
    } else if (isEmbeddedChat) {
      onInvalidChat?.(chatId);
    } else {
      navigate('/');
    }
  }, [chatId, initializeChat, isEmbeddedChat, navigate, onInvalidChat]);

  useEffect(() => {
    if (!chatId || !currentChat) {
      return;
    }

    if (
      selectedModelId !== currentChat.modelId ||
      arenaSecondModelId !== (currentChat.modelId2 ?? null) ||
      chatTitle !== currentChat.title
    ) {
      initializeChat(chatId);
    }
  }, [chatId, currentChat, selectedModelId, arenaSecondModelId, chatTitle, initializeChat]);

  useEffect(() => {
    if (chatId && (currentChat?.type === 'arena' || currentChat?.type === 'collab')) {
      const { fetchChatMessages } = useSupabaseStore.getState();
      fetchChatMessages(chatId);
    }
  }, [chatId, currentChat?.type]);

  useEffect(() => {
    if (messages.length > 0 && arenaMode) {
      const arenaMessages = messages.filter(m => m.is_arena);
      const userMessagesWithMissingOrder = arenaMessages.filter(m => 
        m.role === 'user' && (m.arena_order === undefined || m.arena_order === null)
      );

      if (userMessagesWithMissingOrder.length > 0) {
        const updatedMessages = messages.map(msg => {
          if (msg.role === 'user' && msg.is_arena && (msg.arena_order === undefined || msg.arena_order === null)) {
            return { ...msg, arena_order: 0 };
          }
          return msg;
        });

        setMessages(updatedMessages);

        useSupabaseStore.setState(state => ({
          chats: state.chats.map(chat =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: updatedMessages,
                }
              : chat
          ),
        }));
      }
    }
  }, [messages, arenaMode, chatId, setMessages]);

  useEffect(() => {
    if (messages.length > 0 && arenaMode) {
      const arenaMessages = messages.filter(m => m.is_arena);
      const groups: Record<string, { userMessage?: Message; primaryResponse?: Message; secondaryResponse?: Message }> = {};

      for (const msg of arenaMessages) {
        if (!msg.arena_group_id) {
            continue;
        }
        if (!groups[msg.arena_group_id]) {
            groups[msg.arena_group_id] = {};
        }
        if (msg.arena_order === 0) {
          groups[msg.arena_group_id].userMessage = msg;
        } else if (msg.arena_order === 1) {
          groups[msg.arena_group_id].primaryResponse = msg;
        } else if (msg.arena_order === 2) {
          groups[msg.arena_group_id].secondaryResponse = msg;
        }
      }

      const pairs = Object.values(groups)
        .filter(
          (
            pair
          ): pair is {
            userMessage: Message;
            primaryResponse?: Message;
            secondaryResponse?: Message;
          } => Boolean(pair.userMessage)
        )
        .sort((a, b) => {
          const t1 = new Date(a.userMessage.timestamp || 0).getTime();
          const t2 = new Date(b.userMessage.timestamp || 0).getTime();
          return t1 - t2;
        });

      setArenaMessagePairs(pairs);
    } else if (!arenaMode) {
      console.log('[Chat.tsx] Not in Arena mode or no messages, clearing arenaMessagePairs.');
      setArenaMessagePairs([]);
    }
  }, [messages, arenaMode]);

  const [arenaMessagePairs, setArenaMessagePairs] = useState<
    Array<{
      userMessage: Message;
      primaryResponse?: Message;
      secondaryResponse?: Message;
    }>
  >([]);

  const [isCodeEditorOpen, setIsCodeEditorOpen] = useState(false);
  const [isCodeHistoryOpen, setIsCodeHistoryOpen] = useState(false);
  const [isCanvasOpen, setIsCanvasOpen] = useState(false);
  const [isChatHistoryOpen, setIsChatHistoryOpen] = useState(false);
  const codeSnippets = useRef<any[]>([]);
  const { fetchCodeSnippets } = useCodeSnippets(addMessageToDb);

  const fetchSnippets = useCallback(async () => {
    if (isCodeHistoryOpen || isCodeEditorOpen) {
      const snippets = await fetchCodeSnippets();
      const mapped = (snippets || []).map((snippet: any) => ({
        id: snippet.id,
        code: snippet.code,
        language: snippet.language,
        name: snippet.name,
        chatId: snippet.chat_id,
        timestamp: snippet.timestamp || snippet.created_at || new Date().toISOString(),
        userId: snippet.user_id,
        content: snippet.code,
      }));
      codeSnippets.current = mapped;
    }
  }, [isCodeHistoryOpen, isCodeEditorOpen, fetchCodeSnippets]);
  useEffect(() => { fetchSnippets(); }, [fetchSnippets]);

  useEffect(() => {
    if (arenaMode && !arenaSecondModelId && availableModels.length > 1) {
      const alt = availableModels.find(m => m.id !== selectedModelId);
      if (alt) handleModel2Change(alt.id);
    }
    if (!arenaMode) {
      setArenaMessagePairs([]);
    }
  }, [arenaMode, availableModels, selectedModelId, arenaSecondModelId, handleModel2Change]);

  useEffect(() => {
    if (collabMode && !collabConfig && availableModels.length > 0) {
      void saveCollabConfig(createDefaultCollabConfig(availableModels, selectedModelId || undefined));
    }
  }, [availableModels, collabConfig, collabMode, saveCollabConfig, selectedModelId]);

  const persistChatMode = useCallback(async (mode: ChatType['type']) => {
    if (!chatId) return;
    (updateChat as any)(chatId, { type: mode });
    if (useAppStore.getState().saveHistory) {
      const { error } = await (supabase as any).from('chats').update({ type: mode }).eq('id', chatId);
      if (error) {
        console.error('Failed to update chat type', error);
      }
    }
  }, [chatId, updateChat]);

  const handleSelectMode = useCallback(async (mode: 'user-ai' | 'arena' | 'collab') => {
    if (!chatId) return;

    if (mode === 'collab' && !collabConfig && availableModels.length > 0) {
      await saveCollabConfig(createDefaultCollabConfig(availableModels, selectedModelId || undefined));
    }

    await persistChatMode(mode);
  }, [availableModels, chatId, collabConfig, persistChatMode, saveCollabConfig, selectedModelId]);

  const handleSaveCollabSetup = useCallback(async (config: typeof normalizedCollabConfig) => {
    await saveCollabConfig(config);
  }, [saveCollabConfig]);

  const handleToggleDeepResearch = useCallback(async (enabled: boolean) => {
    const nextConfig = normalizeCollabConfig(
      {
        ...normalizedCollabConfig,
        workflow: enabled ? 'deep_research' : 'standard',
        nodes: enabled ? [] : normalizedCollabConfig.nodes,
      },
      availableModels,
      selectedModelId || undefined
    );
    await saveCollabConfig(nextConfig);
  }, [availableModels, normalizedCollabConfig, saveCollabConfig, selectedModelId]);

  const handleSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (collabMode) {
      await handleCollabSendMessage(content, attachments);
      return;
    }

    if (arenaMode) {
      await handleArenaSendMessage(content, attachments);
      return;
    }

    if (!selectedModelId) {
      toast({ title: "Select a model", description: "Please select an AI model before sending a message.", variant: "destructive" });
      return;
    }

    await handleNormalInput(content, attachments);
  };

  const handleArenaSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (!chatId || !authUser) return;
    if (!selectedModelId || !arenaSecondModelId) {
      toast({ title: "Select two models", description: "Please select both models for Arena mode.", variant: "destructive" });
      return;
    }
    setPendingArena(true);

    const arenaGroupId = uuidv4();
    const { addMessageLocally, updateMessageLocally } = useSupabaseStore.getState();

    const primaryModel = availableModels.find(m => m.id === selectedModelId);
    const secondaryModel = availableModels.find(m => m.id === arenaSecondModelId);

    if (!primaryModel || !secondaryModel) {
      toast({ title: "Model error", description: "Could not find both models.", variant: "destructive" });
      setPendingArena(false);
      return;
    }

    const tempUserMessageId = uuidv4();
    const tempPrimaryResponseId = uuidv4();
    const tempSecondaryResponseId = uuidv4();

    let userMessage: Message = {
      id: tempUserMessageId,
      content,
      role: 'user',
      sender: 'user',
      timestamp: new Date().toISOString(),
      attachments,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: null,
      arena_order: 0,
    } as any;

    const pendingPrimaryResponse: Message = {
      id: tempPrimaryResponseId,
      content: '',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: primaryModel.id,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: primaryModel.id,
      arena_order: 1,
    } as any;

    const pendingSecondaryResponse: Message = {
      id: tempSecondaryResponseId,
      content: '',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: secondaryModel.id,
      chatId,
      is_arena: true,
      arena_group_id: arenaGroupId,
      arena_model: secondaryModel.id,
      arena_order: 2,
    } as any;

    addMessageLocally(chatId, userMessage);
    addMessageLocally(chatId, pendingPrimaryResponse);
    addMessageLocally(chatId, pendingSecondaryResponse);

    try {
      const persistedUserMessage = await addMessageToDb(chatId, {
        content,
        role: 'user',
        sender: 'user',
        attachments,
        is_arena: true,
        arena_group_id: arenaGroupId,
        arena_model: null,
        arena_order: 0,
      });

      if (!persistedUserMessage) {
        throw new Error('Failed to save Arena prompt.');
      }

      userMessage = persistedUserMessage;
      updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);

      const projectAiContext = await getProjectAiContext(currentProjectId);
      const baseMessages: Message[] = projectAiContext
        ? [
            {
              id: uuidv4(),
              content: projectAiContext.systemPrompt,
              role: 'system',
              sender: 'ai',
              timestamp: new Date().toISOString(),
              chatId,
            } as Message,
            userMessage,
          ]
        : [userMessage];

      const runArenaModel = async (
        arenaModel: AIModel,
        order: 1 | 2,
        tempMessageId: string,
        basePendingMessage: Message
      ) => {
        try {
          const result = await sendMessageToModel(baseMessages, arenaModel, availableModels);
          const resolvedContent = result.content?.trim() || `No response returned from ${arenaModel.name}.`;

          if ((result.tokensUsed || 0) > 0) {
            await addTokensUsed(chatId, result.tokensUsed || 0);
          }

          const persistedResponse = await addMessageToDb(chatId, {
            content: resolvedContent,
            role: 'assistant',
            sender: 'ai',
            model: arenaModel.id,
            is_arena: true,
            arena_group_id: arenaGroupId,
            arena_model: arenaModel.id,
            arena_order: order,
          });

          if (!persistedResponse) {
            throw new Error(`Failed to save ${arenaModel.name} response.`);
          }

          updateMessageLocally(chatId, tempMessageId, persistedResponse);
        } catch (error: any) {
          updateMessageLocally(chatId, tempMessageId, {
            ...basePendingMessage,
            content: `Unable to get a response from ${arenaModel.name}${error?.message ? `: ${error.message}` : '.'}`,
          });
        }
      };

      await Promise.allSettled([
        runArenaModel(primaryModel, 1, tempPrimaryResponseId, pendingPrimaryResponse),
        runArenaModel(secondaryModel, 2, tempSecondaryResponseId, pendingSecondaryResponse),
      ]);
    } catch (err) {
      useSupabaseStore.setState(state => ({
        chats: state.chats.map(chat =>
          chat.id === chatId
            ? {
                ...chat,
                messages: (chat.messages || []).filter(
                  msg => ![tempUserMessageId, tempPrimaryResponseId, tempSecondaryResponseId].includes(msg.id)
                ),
              }
            : chat
        ),
      }));
      toast({ title: "Arena error", description: "Failed to get responses from both models or save to database.", variant: "destructive" });
    } finally {
      setPendingArena(false);
    }
  };

  const handleCollabSendMessage = async (content: string, attachments?: Attachment[]) => {
    if (!chatId || !authUser) return;

    const validation = validateCollabConfig(normalizedCollabConfig, availableModels);
    if (!validation.isValid) {
      toast({
        title: 'Finish your collab setup',
        description: validation.errors[0] || 'Complete the collab workflow before sending.',
        variant: 'destructive',
      });
      setIsCollabConfigOpen(true);
      return;
    }

    if (normalizedCollabConfig.workflow === 'deep_research') {
      setPendingCollab(true);
      try {
        await runDeepResearchCollab({
          content,
          attachments,
          user: authUser,
          availableModels,
          currentProjectId,
          knowledgeBaseId: projectKbId,
          knowledgeBaseName: projectName,
          collabConfig: normalizedCollabConfig,
        });
      } catch (error: any) {
        toast({
          title: 'Deep Research error',
          description: error?.message || 'Failed to run the Deep Research workflow.',
          variant: 'destructive',
        });
      } finally {
        setPendingCollab(false);
      }
      return;
    }

    const drafterNodes = normalizedCollabConfig.nodes.filter((node) => node.role === 'drafter');
    const editorNode = normalizedCollabConfig.nodes.find((node) => node.role === 'editor');
    const drafterModels = drafterNodes
      .map((node) => ({ node, model: availableModels.find((candidate) => candidate.id === node.modelId) || null }))
      .filter((entry): entry is { node: typeof drafterNodes[number]; model: AIModel } => Boolean(entry.model));
    const editorModel = availableModels.find((candidate) => candidate.id === editorNode?.modelId) || null;

    if (drafterModels.length === 0 || !editorModel) {
      toast({
        title: 'Unavailable collab model',
        description: 'One or more saved collab models are no longer available. Update the setup and try again.',
        variant: 'destructive',
      });
      setIsCollabConfigOpen(true);
      return;
    }

    setPendingCollab(true);
    const collabGroupId = uuidv4();
    const { addMessageLocally, updateMessageLocally } = useSupabaseStore.getState();
    const now = new Date().toISOString();

    const tempUserMessageId = uuidv4();
    const tempEditorMessageId = uuidv4();
    const tempDrafterMessageIds = drafterModels.map(() => uuidv4());

    const tempUserMessage: Message = {
      id: tempUserMessageId,
      chatId,
      content,
      role: 'user',
      sender: authUser.id,
      timestamp: now,
      attachments,
      is_collab: true,
      collab_group_id: collabGroupId,
      collab_role: 'user_prompt',
      collab_order: 0,
      collab_status: 'complete',
    };

    const pendingDrafterMessages: Message[] = drafterModels.map(({ node, model }, index) => ({
      id: tempDrafterMessageIds[index],
      chatId,
      content: '',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: model.id,
      is_collab: true,
      collab_group_id: collabGroupId,
      collab_role: 'drafter',
      collab_model: model.id,
      collab_order: index + 1,
      collab_status: 'pending',
    }));

    const pendingEditorMessage: Message = {
      id: tempEditorMessageId,
      chatId,
      content: '',
      role: 'assistant',
      sender: 'ai',
      timestamp: new Date().toISOString(),
      model: editorModel.id,
      is_collab: true,
      collab_group_id: collabGroupId,
      collab_role: 'editor',
      collab_model: editorModel.id,
      collab_order: drafterModels.length + 1,
      collab_status: 'pending',
    };

    addMessageLocally(chatId, tempUserMessage);
    pendingDrafterMessages.forEach((message) => addMessageLocally(chatId, message));
    addMessageLocally(chatId, pendingEditorMessage);

    try {
      const persistedUserMessage = await addMessageToDb(chatId, {
        content,
        role: 'user',
        sender: authUser.id,
        attachments,
        is_collab: true,
        collab_group_id: collabGroupId,
        collab_role: 'user_prompt',
        collab_order: 0,
        collab_status: 'complete',
      });

      if (!persistedUserMessage) {
        throw new Error('Failed to save the collab prompt.');
      }

      updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);

      const projectAiContext = await getProjectAiContext(currentProjectId);
      const baseMessages: Message[] = projectAiContext
        ? [
            {
              id: uuidv4(),
              content: projectAiContext.systemPrompt,
              role: 'system',
              sender: 'ai',
              timestamp: new Date().toISOString(),
              chatId,
            } as Message,
            persistedUserMessage,
          ]
        : [persistedUserMessage];

      const draftResults = await Promise.all(
        drafterModels.map(async ({ model }, index) => {
          const tempId = tempDrafterMessageIds[index];
          const pendingMessage = pendingDrafterMessages[index];

          try {
            const result = await sendMessageToModel(baseMessages, model, availableModels);
            const resolvedContent = result.content?.trim() || `No response returned from ${model.name}.`;

            if ((result.tokensUsed || 0) > 0) {
              await addTokensUsed(chatId, result.tokensUsed || 0);
            }

            const persistedDraft = await addMessageToDb(chatId, {
              content: resolvedContent,
              role: 'assistant',
              sender: 'ai',
              model: model.id,
              is_collab: true,
              collab_group_id: collabGroupId,
              collab_role: 'drafter',
              collab_model: model.id,
              collab_order: index + 1,
              collab_status: 'complete',
            });

            if (!persistedDraft) {
              throw new Error(`Failed to save ${model.name}'s draft.`);
            }

            updateMessageLocally(chatId, tempId, persistedDraft);
            return { ok: true as const, model, message: persistedDraft };
          } catch (error: any) {
            const errorText = `Unable to get a draft from ${model.name}${error?.message ? `: ${error.message}` : '.'}`;
            const persistedError = await addMessageToDb(chatId, {
              content: errorText,
              role: 'assistant',
              sender: 'ai',
              model: model.id,
              is_collab: true,
              collab_group_id: collabGroupId,
              collab_role: 'drafter',
              collab_model: model.id,
              collab_order: index + 1,
              collab_status: 'error',
            });

            updateMessageLocally(chatId, tempId, persistedError || { ...pendingMessage, content: errorText, collab_status: 'error' });
            return { ok: false as const, model, error: errorText };
          }
        })
      );

      const successfulDrafts = draftResults.filter((result): result is { ok: true; model: AIModel; message: Message } => result.ok);

      if (successfulDrafts.length === 0) {
        const errorText = 'The editor could not run because all drafter models failed.';
        const persistedEditorError = await addMessageToDb(chatId, {
          content: errorText,
          role: 'assistant',
          sender: 'ai',
          model: editorModel.id,
          is_collab: true,
          collab_group_id: collabGroupId,
          collab_role: 'editor',
          collab_model: editorModel.id,
          collab_order: drafterModels.length + 1,
          collab_status: 'error',
        });
        updateMessageLocally(chatId, tempEditorMessageId, persistedEditorError || { ...pendingEditorMessage, content: errorText, collab_status: 'error' });
        return;
      }

      const editorInstruction: Message[] = [
        ...(projectAiContext
          ? [
              {
                id: uuidv4(),
                chatId,
                role: 'system',
                sender: 'ai',
                timestamp: new Date().toISOString(),
                content: projectAiContext.systemPrompt,
              } as Message,
            ]
          : []),
        {
          id: uuidv4(),
          chatId,
          role: 'system',
          sender: 'ai',
          timestamp: new Date().toISOString(),
          content:
            'You are the editor in a collaboration pipeline. Review the drafts, keep the strongest ideas, resolve disagreements, and produce one polished final answer for the user. Do not mention internal pipeline mechanics unless helpful.',
        },
        {
          id: uuidv4(),
          chatId,
          role: 'user',
          sender: authUser.id,
          timestamp: new Date().toISOString(),
          content: [
            `Original user prompt:\n${content}`,
            'Candidate drafts:',
            ...successfulDrafts.map((draft, index) => `Draft ${index + 1} — ${draft.model.name}:\n${draft.message.content}`),
          ].join('\n\n'),
        },
      ];

      try {
        const editorResult = await sendMessageToModel(editorInstruction, editorModel, availableModels);
        const editorContent = editorResult.content?.trim() || `No response returned from ${editorModel.name}.`;

        if ((editorResult.tokensUsed || 0) > 0) {
          await addTokensUsed(chatId, editorResult.tokensUsed || 0);
        }

        const persistedEditorMessage = await addMessageToDb(chatId, {
          content: editorContent,
          role: 'assistant',
          sender: 'ai',
          model: editorModel.id,
          is_collab: true,
          collab_group_id: collabGroupId,
          collab_role: 'editor',
          collab_model: editorModel.id,
          collab_order: drafterModels.length + 1,
          collab_status: 'complete',
        });

        if (!persistedEditorMessage) {
          throw new Error('Failed to save the editor response.');
        }

        updateMessageLocally(chatId, tempEditorMessageId, persistedEditorMessage);
      } catch (error: any) {
        const errorText = `Unable to get a final answer from ${editorModel.name}${error?.message ? `: ${error.message}` : '.'}`;
        const persistedEditorError = await addMessageToDb(chatId, {
          content: errorText,
          role: 'assistant',
          sender: 'ai',
          model: editorModel.id,
          is_collab: true,
          collab_group_id: collabGroupId,
          collab_role: 'editor',
          collab_model: editorModel.id,
          collab_order: drafterModels.length + 1,
          collab_status: 'error',
        });
        updateMessageLocally(chatId, tempEditorMessageId, persistedEditorError || { ...pendingEditorMessage, content: errorText, collab_status: 'error' });
      }
    } catch (error: any) {
      useSupabaseStore.setState(state => ({
        chats: state.chats.map(chat =>
          chat.id === chatId
            ? {
                ...chat,
                messages: (chat.messages || []).filter(
                  msg => ![tempUserMessageId, tempEditorMessageId, ...tempDrafterMessageIds].includes(msg.id)
                ),
              }
            : chat
        ),
      }));
      toast({ title: 'Collab error', description: error?.message || 'Failed to run the collab pipeline.', variant: 'destructive' });
    } finally {
      setPendingCollab(false);
    }
  };

  const [messagesLoaded, setMessagesLoaded] = useState(false);
  useEffect(() => {
    if (chatId && messages.length > 0) {
      if (!messagesLoaded) setMessagesLoaded(true);
    }
  }, [messages, chatId, messagesLoaded]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId) && !viewCountTracked.current) {
      incrementChatViews(chatId);
      viewCountTracked.current = true;
    }
  }, [chatId, incrementChatViews]);

  const persistedArenaPairs = useMemo(() => {
    const arenaMessages = messages.filter(m => m.is_arena);
    const groups: Record<string, { userMessage?: Message; primaryResponse?: Message; secondaryResponse?: Message }> = {};
    for (const msg of arenaMessages) {
      if (!msg.arena_group_id) continue;
      if (!groups[msg.arena_group_id]) groups[msg.arena_group_id] = {};
      if (msg.arena_order === 0) {
        groups[msg.arena_group_id].userMessage = msg;
      } else if (msg.arena_order === 1) {
        groups[msg.arena_group_id].primaryResponse = msg;
      } else if (msg.arena_order === 2) {
        groups[msg.arena_group_id].secondaryResponse = msg;
      }
    }
    return Object.values(groups)
      .filter(pair => pair.userMessage)
      .sort((a, b) => {
        const t1 = new Date(a.userMessage?.timestamp || 0).getTime();
        const t2 = new Date(b.userMessage?.timestamp || 0).getTime();
        return t1 - t2;
      });
  }, [messages]);

  const collabMessageGroups = useMemo(() => {
    const groups: Record<string, { id: string; userMessage?: Message; drafterMessages: Message[]; editorMessage?: Message; assistantMessages: Message[] }> = {};

    for (const message of messages.filter((item) => item.is_collab)) {
      if (!message.collab_group_id) continue;
      if (!groups[message.collab_group_id]) {
        groups[message.collab_group_id] = {
          id: message.collab_group_id,
          drafterMessages: [],
          assistantMessages: [],
        };
      }

      if (message.collab_role === 'user_prompt') {
        groups[message.collab_group_id].userMessage = message;
      } else {
        groups[message.collab_group_id].assistantMessages.push(message);
        if (message.collab_role === 'drafter') {
          groups[message.collab_group_id].drafterMessages.push(message);
        } else if (message.collab_role === 'editor') {
          groups[message.collab_group_id].editorMessage = message;
        }
      }
    }

    return Object.values(groups)
      .filter((group) => group.userMessage)
      .map((group) => ({
        ...group,
        drafterMessages: [...group.drafterMessages].sort((a, b) => {
          const orderDelta = (a.collab_order ?? 0) - (b.collab_order ?? 0);
          if (orderDelta !== 0) return orderDelta;
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        }),
        assistantMessages: [...group.assistantMessages].sort((a, b) => {
          const orderDelta = (a.collab_order ?? 0) - (b.collab_order ?? 0);
          if (orderDelta !== 0) return orderDelta;
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        }),
      }))
      .sort((a, b) => {
        const t1 = new Date(a.userMessage?.timestamp || 0).getTime();
        const t2 = new Date(b.userMessage?.timestamp || 0).getTime();
        return t1 - t2;
      });
  }, [messages]);

  const nonSpecialMessages = useMemo(() => messages.filter(m => !m.is_arena && !m.is_collab), [messages]);

  const handleRegenerateResponse = async (messageId: string) => {
    const messageIndex = messages.findIndex(m => m.id === messageId);

    if (messageIndex <= 0) {
      toast({ title: "Error", description: "Could not find the original prompt to regenerate.", variant: "destructive" });
      return;
    }

    const aiMessage = messages[messageIndex];
    const userPrompt = messages[messageIndex - 1];

    if (aiMessage.role !== 'assistant' || userPrompt.role !== 'user') {
      toast({ title: "Invalid Action", description: "Only AI responses to a user's prompt can be regenerated.", variant: "destructive" });
      return;
    }

    const updatedMessages = messages.filter(m => m.id !== messageId);
    setMessages(updatedMessages);

    const { error: deleteError } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);

    if (deleteError) {
      toast({ title: "Database Error", description: `Failed to delete the previous response: ${deleteError.message}`, variant: "destructive" });
      setMessages(messages);
      return;
    }

    await sendMessage(userPrompt.content, userPrompt.attachments);
  };

  const renderArenaMessages = () => (
    <div className="flex-1 overflow-y-auto px-2 py-4">
      <div className="flex flex-col gap-6">
        {[...persistedArenaPairs, ...arenaMessagePairs.filter(p => !persistedArenaPairs.some(pp => pp.userMessage?.id === p.userMessage?.id))]
          .map((pair, idx) => (
          <div key={pair.userMessage?.id || idx} className="border rounded-lg bg-muted/10 p-4">
            <div className="mb-2">
              <span className="font-semibold">You:</span>
              <div className="ml-2 text-sm">{pair.userMessage?.content}</div>
            </div>
            <div className="flex flex-col gap-4">
              <div className="border rounded bg-background p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{availableModels.find(m => m.id === selectedModelId)?.name || "Model 1"}</Badge>
                </div>
                <div className="text-sm whitespace-pre-line prose dark:prose-invert max-w-none">
                  {pair.primaryResponse?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {cleanMarkdownBold(pair.primaryResponse.content)}
                    </ReactMarkdown>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground not-prose">
                      <span>Waiting for response</span>
                      <TypingIndicator />
                    </div>
                  )}
                </div>
              </div>
              <div className="border rounded bg-background p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline">{availableModels.find(m => m.id === arenaSecondModelId)?.name || "Model 2"}</Badge>
                </div>
                <div className="text-sm whitespace-pre-line prose dark:prose-invert max-w-none">
                  {pair.secondaryResponse?.content ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {cleanMarkdownBold(pair.secondaryResponse.content)}
                    </ReactMarkdown>
                  ) : (
                    <div className="flex items-center gap-2 text-muted-foreground not-prose">
                      <span>Waiting for response</span>
                      <TypingIndicator />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
        {pendingArena && (
          <div className="flex items-center justify-center gap-2 text-center text-muted-foreground py-4">
            <span>Waiting for both models to respond</span>
            <TypingIndicator />
          </div>
        )}
        <div ref={messagesEndRef} className="h-0" />
      </div>
    </div>
  );

  const renderCollabMessages = () => (
    <div className="flex-1 overflow-y-auto px-2 py-4">
      <div className="flex flex-col gap-6">
        {collabMessageGroups.map((group) => (
          <CollabMessageCard key={group.id} group={group} models={availableModels} />
        ))}
        <div ref={messagesEndRef} className="h-0" />
      </div>
    </div>
  );

  const renderModeControls = () => (
    <div className="border-b bg-muted/20 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant={!arenaMode && !collabMode ? 'default' : 'outline'} size="sm" onClick={() => void handleSelectMode('user-ai')}>
          Standard
        </Button>
        <Button
          variant={arenaMode ? 'default' : 'outline'}
          size="sm"
          className={arenaMode ? 'bg-purple-600 text-white' : ''}
          onClick={() => void handleSelectMode(arenaMode ? 'user-ai' : 'arena')}
          disabled={isMobile}
        >
          Arena
        </Button>
        <Button
          variant={collabMode ? 'default' : 'outline'}
          size="sm"
          className={collabMode ? 'bg-sky-600 text-white' : ''}
          onClick={() => void handleSelectMode(collabMode ? 'user-ai' : 'collab')}
        >
          <Users className="mr-1 h-4 w-4" />
          Collab
        </Button>

        {arenaMode ? (
          <div className="ml-auto flex items-center gap-2">
            <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
              Arena Mode
            </Badge>
            <span className="text-xs text-muted-foreground">Second model</span>
            <ModelSelector
              models={availableModels.filter(m => m.id !== selectedModelId)}
              selectedModelId={arenaSecondModelId || ''}
              onModelChange={handleModel2Change}
            />
          </div>
        ) : null}

        {collabMode ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={deepResearchEnabled ? 'border-fuchsia-300 bg-fuchsia-100 text-fuchsia-700' : 'bg-sky-100 text-sky-700 border-sky-300'}>
              {collabSummary.label}
            </Badge>
            {collabSummary.participantSummaries.slice(0, deepResearchEnabled ? 3 : collabSummary.drafterModelNames.length).map((participant) => (
              <Badge key={`${participant.role}-${participant.label}`} variant="secondary">{participant.modelName}</Badge>
            ))}
            {!deepResearchEnabled ? (
              <Badge className="bg-violet-600 text-white hover:bg-violet-600">{collabSummary.editorModelName}</Badge>
            ) : null}
            <Button type="button" size="sm" variant="outline" onClick={() => setIsCollabConfigOpen(true)}>
              <Settings2 className="mr-1 h-4 w-4" />
              {deepResearchEnabled ? 'Configure Deep Research' : 'Configure Collab'}
            </Button>
            {!collabValidation.isValid ? (
              <Badge variant="destructive">Setup incomplete</Badge>
            ) : null}
          </div>
        ) : null}

        {isMobile && arenaMode ? (
          <span className="text-xs text-muted-foreground">Arena mode is not available on mobile.</span>
        ) : null}
      </div>
    </div>
  );

  const handleArenaInput = (content: string, attachments?: Attachment[]) => {
    handleArenaSendMessage(content, attachments);
  };

  const handleCollabInput = (content: string, attachments?: Attachment[]) => {
    handleCollabSendMessage(content, attachments);
  };

  const handleNormalInput = async (content: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null) => {
    const useKbId = projectKbId ?? kbId ?? null;
    const useKbName = projectName ?? kbName ?? null;
    const thinkingConfig: AIConfig | undefined = thinkingAvailable && thinkingEnabled
      ? {
          enableThinking: true,
          ...(selectedModel?.provider === 'transformers' ? { max_tokens: 1024 } : {}),
        }
      : undefined;
    await sendMessage(content, attachments, useKbId, useKbName, authUser, thinkingConfig);
  };

  const inputHandler = collabMode ? handleCollabInput : arenaMode ? handleArenaInput : handleNormalInput;
  const standardChatGenerating = !arenaMode && !collabMode && isGenerating && activeRequestChatId === chatId;
  const composerLoading = arenaMode ? pendingArena : collabMode ? pendingCollab : isLoading;

  const [trustEnabled, setTrustEnabled] = useState<boolean>(false);

  useEffect(() => {
    const saved = localStorage.getItem('trust_mode_enabled');
    setTrustEnabled(saved === 'true');
  }, []);

  const handleToggleTrust = (value: boolean) => {
    setTrustEnabled(value);
    localStorage.setItem('trust_mode_enabled', value ? 'true' : 'false');
  };

  const handleOpenChatInMapleWord = useCallback(async () => {
    if (!chatId || !currentChat) return;

    const folder = await enableChatVFSAccess(
      { id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any,
      messages
    );
    const convPath = `${folder}\\conversation.txt`;
    const title = `MapleWord - ${currentChat.title || chatTitle || 'Chat'}`;
    const url = `/desktop/start?open=maple-word&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(convPath)}`;
    window.location.assign(url);
  }, [chatId, currentChat, chatTitle, messages]);

  const renderChatHeader = () => (
    <div className="flex-shrink-0 bg-background border-b">
      <SimpleChatHeader 
        title={chatTitle}
        modelName={modelName}
        modelId={selectedModelId}
        onTitleChange={handleTitleChange}
        onDeleteChat={handleDeleteChat}
        projectId={currentProjectId}
      />
      {renderModeControls()}
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2">
        <div className="min-w-0 flex-1 sm:flex-none">
          <ModelSelector
            models={availableModels}
            selectedModelId={selectedModelId || ''}
            onModelChange={handleModelSelectorChange}
            disabled={collabMode}
          />
        </div>
        {collabMode ? (
          <span className="text-xs text-muted-foreground">
            {deepResearchEnabled
              ? 'Model assignment is handled per Deep Research role in the collab workflow.'
              : 'Base model selection is managed by your Collab participants.'}
          </span>
        ) : null}
        {thinkingAvailable && (
          <div className={`${headerToggleCardClassName} shrink-0`}>
            <span className="text-sm font-medium whitespace-nowrap">Thinking</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 p-0 hover:bg-muted">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    When enabled, reasoning-capable models use a deeper reasoning path. If the provider exposes visible thinking, it will be attached to the assistant reply. Otherwise MapleOS shows an honest Thinking status while the answer is generated.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch
              className={visibleSwitchClassName}
              checked={thinkingEnabled}
              onCheckedChange={(value) => {
                setThinkingEnabled(value);
                saveThinkingEnabled(selectedModel?.id ?? null, value);
              }}
            />
          </div>
        )}
        <div className={`${headerToggleCardClassName} shrink-0`}>
          <span className="text-sm font-medium whitespace-nowrap">Trust Mode</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6 p-0 hover:bg-muted">
                  <Info className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p className="text-xs">
                  When ON, prompts are checked for authorship, clarity, and presence. Roleplay/simulation is blocked, sensitive info is redacted, and generation parameters are capped for safer, consistent results.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Switch className={visibleSwitchClassName} checked={trustEnabled} onCheckedChange={handleToggleTrust} />
        </div>
        {collabMode ? (
          <div className={`${headerToggleCardClassName} shrink-0`}>
            <span className="text-sm font-medium whitespace-nowrap">Research</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-6 w-6 p-0 hover:bg-muted">
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">Deep Research is available only in Collab mode and uses one assigned model per research role.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Switch className={visibleSwitchClassName} checked={deepResearchEnabled} onCheckedChange={(value) => void handleToggleDeepResearch(value)} />
          </div>
        ) : null}
      </div>

      {showLocalModelBanner ? (
        <div className="border-b px-4 py-3 bg-muted/20">
          <LocalModelStatusCard modelName={selectedModel?.name} compact />
        </div>
      ) : null}

      <div className="flex items-center gap-3 px-4 py-2 border-b bg-muted/30">
        <span className="text-xs text-muted-foreground">Open in MapleOS:</span>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2"
          onClick={handleOpenChatInMapleWord}
          title="Open in MapleWord"
        >
          <FileText size={16} className="" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 cursor-not-allowed pointer-events-none opacity-50"
          onClick={async () => {
            if (!chatId || !currentChat) return;
            const folder = await enableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any, messages);
            const convPath = `${folder}\\conversation.txt`;
            const title = `MapleSlide - ${currentChat.title || chatTitle || 'Chat'}`;
            const url = `/desktop/start?open=maple-slide&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(convPath)}`;
            window.open(url, '_blank');
          }}
          title="Open in MapleSlide"
        >
          <Presentation className="w-5 h-5" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 opacity-50 cursor-not-allowed pointer-events-none"
          onClick={async () => {
            if (!chatId || !currentChat) return;
            const folder = await enableChatVFSAccess({ id: chatId, title: currentChat.title || chatTitle || 'Chat' } as any, messages);
            const convPath = `${folder}\\conversation.txt`;
            const title = `MapleSheet - ${currentChat.title || chatTitle || 'Chat'}`;
            const url = `/desktop/start?open=maple-sheet&title=${encodeURIComponent(title)}&filePath=${encodeURIComponent(convPath)}`;
            window.open(url, '_blank');
          }}
          title="Open in MapleSheet"
        >
          <Sheet className="w-5 h-5" />
        </Button>
      </div>
    </div>
  );

  const handleInsertSnippetToChat = useCallback(
    async (snippet) => {
      if (!chatId) return;

      const attachment: Attachment = {
        id: Math.random().toString(36).substring(2, 11),
        type: 'code',
        content: snippet.code,
        name: snippet.name || `Code snippet (${snippet.language})`,
      };

      const message = `I've shared a code snippet in ${snippet.language}. Please analyze the attached code.`;

      await handleSendMessage(message, [attachment]);
      setIsCodeEditorOpen(false);
    },
    [chatId, handleSendMessage]
  );

  const handleModelSelectorChange = useCallback(
    (modelId: string) => {
      handleModelChange(modelId);
    },
    [handleModelChange]
  );

  useEffect(() => {
    if (chatInGlobalStore?.messages) {
      setMessages(chatInGlobalStore.messages);
    }
  }, [chatInGlobalStore?.messages, setMessages]);

  useEffect(() => {
    if (chatId && isValidUUID(chatId)) {
      initializeChat(chatId);
    } else if (isEmbeddedChat) {
      onInvalidChat?.(chatId);
    } else {
      navigate('/');
    }
  }, [chatId, initializeChat, isEmbeddedChat, navigate, onInvalidChat]);

  if (!chatId) {
    return (
      <div className="flex flex-col h-screen bg-background items-center justify-center">
        <div className="p-6 bg-card rounded-lg shadow-lg text-center max-w-md">
          <h2 className="text-xl font-semibold mb-4">Invalid Chat ID</h2>
          <p className="mb-4">The chat ID format is invalid. You will be redirected to the home page shortly.</p>
          <Button asChild className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90">
            <Link to="/">Back to Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full bg-background relative">
        {renderChatHeader()}

        <div className="flex-1 overflow-y-auto min-h-0">
          {collabMode
            ? renderCollabMessages()
            : arenaMode
              ? renderArenaMessages()
              : (nonSpecialMessages.length > 0 ? (
                  <SimpleChatMessageList
                    messages={nonSpecialMessages}
                    isLoading={composerLoading}
                    messagesEndRef={messagesEndRef}
                    chatId={chatId}
                    hideCodeBlockIfAttachment
                    onRegenerate={handleRegenerateResponse}
                    isMemoryUpdating={isMemoryUpdating}
                  />
                ) : composerLoading ? (

                  <div className="flex-1 flex items-center justify-center h-full px-4">
                    {isLocalBrowserModel && ['downloading', 'initializing', 'error', 'webgpu_not_supported'].includes(localLLMStatus.state) ? (
                      <div className="w-full max-w-lg">
                        <LocalModelStatusCard modelName={selectedModel?.name} />
                      </div>
                    ) : (
                      <TypingIndicator />
                    )}
                  </div>
                ) : (

                  <div className="h-full flex items-center justify-center text-center p-8 text-muted-foreground" />

                ))}
        </div>

        <div className="flex-none bg-background border-t p-3 z-10">
          <SimpleChatInput
            onSendMessage={inputHandler}
            onClearChat={clearMessages}
            isLoading={composerLoading}
            isGenerating={standardChatGenerating}
            allowQueueWhileLoading={!arenaMode && !collabMode}
            queuedMessages={!arenaMode && !collabMode ? queuedMessages : []}
            onRemoveQueuedMessage={!arenaMode && !collabMode ? removeQueuedMessage : undefined}
            onStopGeneration={!arenaMode && !collabMode ? stopActiveResponse : undefined}
            onOpenCodeEditor={() => setIsCodeEditorOpen(true)}
            onOpenCanvas={() => setIsCanvasOpen(true)}
            onOpenCodeHistory={() => setIsCodeHistoryOpen(true)}
            onFileUpload={async (file) => {

              if (!chatId) return;
              if (file.size > 10 * 1024 * 1024) {
                toast({
                  title: "File too large",
                  description: "Maximum file size is 10 MB",
                  variant: "destructive"
                });
                return;
              }
              const isImage = file.type.startsWith('image/');
              const isDocument = file.type === 'application/pdf' || 
                              file.type === 'text/plain' || 
                              file.type === 'text/csv' || 
                              file.name.endsWith('.pdf') || 
                              file.name.endsWith('.txt') || 
                              file.name.endsWith('.csv') || 
                              file.name.endsWith('.docx');
              if (!isImage && !isDocument) {
                toast({
                  title: "Unsupported file type",
                  description: "Please upload an image, PDF, TXT, CSV, or DOCX file",
                  variant: "destructive"
                });
                return;
              }
              const reader = new FileReader();
              reader.onload = async (event) => {
                if (event.target?.result) {
                  const attachment: Attachment = {
                    id: Math.random().toString(36).substring(2, 11),
                    type: isImage ? 'image' : 'file',
                    content: event.target.result as string,
                    name: file.name
                  };
                  await inputHandler(`Uploaded ${file.name}`, [attachment]);
                }
              };
              reader.readAsDataURL(file);
            }}
            onExportChat={() => {
              if (nonSpecialMessages.length === 0) {
                return;
              }
              const exportText = nonSpecialMessages.map(msg => {
                const sender = msg.role === 'user' ? 'You' : 'AI';
                return `${sender}: ${msg.content}`;
              }).join('\n\n');
              const blob = new Blob([exportText], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `chat-export-${new Date().toISOString().split('T')[0]}.txt`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            }}
            chatId={chatId}
          />
        </div>
      </div>
      <ChatDialogs
        isCodeEditorOpen={isCodeEditorOpen}
        setIsCodeEditorOpen={setIsCodeEditorOpen}
        isCodeHistoryOpen={isCodeHistoryOpen}
        setIsCodeHistoryOpen={setIsCodeHistoryOpen}
        isCanvasOpen={isCanvasOpen}
        setIsCanvasOpen={setIsCanvasOpen}
        isChatHistoryOpen={isChatHistoryOpen}
        setIsChatHistoryOpen={setIsChatHistoryOpen}
        onSaveCode={async (code, language) => {
          if (!chatId) return;
          const attachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'code',
            content: code,
            name: `Code snippet (${language})`
          };
          const codeBlock = `\`\`\`${language}\n${code}\n\`\`\``;
          await handleSendMessage(
            `I've shared a code snippet in ${language}:\n${codeBlock}`,
            [attachment]
          );
          setIsCodeEditorOpen(false);
        }}
        onSelectCodeSnippet={async (snippetId) => {
          const snippet = codeSnippets.current.find(s => s.id === snippetId);
          if (snippet) {
            const attachment: Attachment = {
              id: Math.random().toString(36).substring(2, 11),
              type: 'code',
              content: snippet.code,
              name: snippet.name
            };
            const codeBlock = `\`\`\`${snippet.language || ''}\n${snippet.code}\n\`\`\``;
            await handleSendMessage(
              `I've shared a code snippet in ${snippet.language}:\n${codeBlock}`,
              [attachment]
            );
            setIsCodeHistoryOpen(false);
          }
        }}
        onSaveCanvas={async (imageData) => {
          const attachment: Attachment = {
            id: Math.random().toString(36).substring(2, 11),
            type: 'canvas',
            content: imageData,
            name: 'Canvas drawing'
          };
          await handleSendMessage("I've shared a drawing:", [attachment]);
          setIsCanvasOpen(false);
        }}
        onSelectChatHistory={() => {
          return;
        }}
        codeSnippets={codeSnippets.current}
        onInsertSnippetToChat={handleInsertSnippetToChat}
      />
      <CollabConfigModal
        open={isCollabConfigOpen}
        onOpenChange={setIsCollabConfigOpen}
        config={normalizedCollabConfig}
        models={availableModels}
        preferredModelId={selectedModelId || undefined}
        onSave={handleSaveCollabSetup}
      />
    </>
  );
};

export default Chat;